import Button, { EButtonVariant, EButtonColor, EButtonSize } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import classes from "./classes.module.scss";
import { useCallback, useEffect, useState } from "react";
import AddMediaModal from "./AddMediaModal";
import MediaContainer from "./MediaContainer";
import EditMediaModal from "./EditMediaModal";
import DeleteMediaModal from "./DeleteMediaModal";
import { useProjectId } from "hooks/useProjectId";
import MediaResponseResource from "common/resources/Project/Config/Media/MediaResponseResource";
import { container } from "tsyringe";
import MediaService from "services/projects/MediaService";

enum EMediaModal {
	ADD = "add",
	EDIT = "edit",
	DELETE = "delete",
}

const mediaService = container.resolve(MediaService);

export default function Media() {
	const { projectId } = useProjectId();
	const [openedModal, setOpenedModal] = useState<EMediaModal | null>(null);
	const [medias, setMedias] = useState<MediaResponseResource[]>([]);
	const [selectedMedia, setSelectedMedia] = useState<MediaResponseResource | null>(null);

	const closeModal = useCallback(() => setOpenedModal(null), []);

	const fetchMedias = useCallback(() => {
		mediaService.getByProjectId(projectId).then((medias) => setMedias(medias));
	}, [projectId]);

	const fetchMediasAndCloseModal = useCallback(() => {
		fetchMedias();
		closeModal();
	}, [fetchMedias, closeModal]);

	const onEditedMedia = useCallback(
		(updatedMedia: MediaResponseResource) => {
			const newMedias = medias.map((media) => (media.id === updatedMedia.id ? updatedMedia : media));
			setMedias(newMedias);
			closeModal();
		},
		[closeModal, medias],
	);

	const onDeletedMedia = useCallback(
		(deletedId: string) => {
			const newMedias = medias.filter((media) => media.id !== deletedId);
			setMedias(newMedias);
			closeModal();
		},
		[closeModal, medias],
	);

	const onEditMediaClick = useCallback((media: MediaResponseResource) => {
		setSelectedMedia(media);
		setOpenedModal(EMediaModal.EDIT);
	}, []);

	const onDeleteMediaClick = useCallback((media: MediaResponseResource) => {
		setSelectedMedia(media);
		setOpenedModal(EMediaModal.DELETE);
	}, []);

	useEffect(() => {
		fetchMedias();
	}, [fetchMedias]);

	const sortedMedias = medias.sort((a, b) => {
		if (a.createdAt === null && b.createdAt === null) return 0;
		if (a.createdAt === null) return 1;
		if (b.createdAt === null) return -1;
		return a.createdAt.getTime() - b.createdAt.getTime();
	});

	return (
		<div className={classes["root"]}>
			<div className={classes["title-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.media.title}
				</Typography>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.SMALL} onClick={() => setOpenedModal(EMediaModal.ADD)}>
					{I18n.asset.pages.professional_space.tabs.media.add_media}
				</Button>
			</div>

			<div>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.media.subtitle}
				</Typography>
				<Typography type="p" size="medium" weight="regular">
					{I18n.asset.pages.professional_space.tabs.media.description}
				</Typography>
			</div>

			<div className={classes["media-container"]}>
				{sortedMedias.map((media) => (
					<MediaContainer key={media.id} media={media} onEditClick={onEditMediaClick} onDeleteClick={onDeleteMediaClick} />
				))}
			</div>

			<AddMediaModal isOpen={openedModal === EMediaModal.ADD} onCreatedMedia={fetchMediasAndCloseModal} onClose={closeModal} projectId={projectId} />
			{selectedMedia && (
				<EditMediaModal isOpen={openedModal === EMediaModal.EDIT} onEditedMedia={onEditedMedia} onClose={closeModal} projectId={projectId} media={selectedMedia} />
			)}
			{selectedMedia && (
				<DeleteMediaModal isOpen={openedModal === EMediaModal.DELETE} onDeletedMedia={onDeletedMedia} onClose={closeModal} media={selectedMedia} projectId={projectId} />
			)}
		</div>
	);
}
